import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { Search } from "@mui/icons-material";
import { Autocomplete, IconButton } from "@mui/material";
import { useDebouncedCallback } from "use-debounce";

import {
  selectAllFolders,
  selectAllProjects,
  selectAllReports,
  selectCurrentAccount,
  useLazyGetFoldersQuery,
  useLazyGetProjectsQuery,
} from "fond/api";
import { SEARCH_KEY } from "fond/constants";
import { Folder, ProjectPreview, Report, Store } from "fond/types";
import { getPath } from "fond/utils/folder";
import { useAppDispatch } from "fond/utils/hooks";
import { searchCollectionByKey } from "fond/utils/search";

import { updateFilter } from "../redux";

import SearchSuggestionsRow from "./SearchSuggestionsRow";
import { MAX_SUGGESTIONS_PER_GROUP } from "./shared";

import { ListSubheader, Paper, SeeAllResultsButton, TextField } from "./Search.styles";

export interface SuggestionsList {
  projects: Array<ProjectPreview & { path: string[] }>;
  folders: Folder[];
  multiProjects: Folder[];
  reports: Array<Report & { path: string[] }>;
}

function getRealFolders(folders: Folder[]): Folder[] {
  return folders.filter((folder) => folder.EntityType === "folder" && !folder.MultiProject && !folder.MultiReport);
}

function getMultiProjectFolders(folders: Folder[]): Folder[] {
  return folders.filter((folder) => folder.EntityType === "folder" && folder.MultiProject);
}

type SearchOption = SuggestionsList["projects"][number] | Folder | { EntityType: "all" } | Report;

type GlobalSearchProps = {
  className?: string;
};
const GlobalSearch: React.FC<GlobalSearchProps> = ({ className }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [value, setValue] = useState<string>("");
  const [suggestionsList, setSuggestionsList] = useState<SuggestionsList>({ projects: [], folders: [], reports: [], multiProjects: [] });
  const [resultCount, setResultCount] = useState<{ [key: string]: number }>({ projects: 0, folders: 0, reports: 0, multiProjects: 0, all: 0 });
  const [open, setOpen] = useState(false);
  const closeOptions = () => setOpen(false);
  const [getFolders, { isLoading: isFoldersLoading, isSuccess: isFoldersLoaded, isError: isFoldersError }] = useLazyGetFoldersQuery(undefined);
  const [getProjects, { isLoading: isProjectsLoading, isSuccess: isProjectsLoaded, isError: isProjectsError }] = useLazyGetProjectsQuery(undefined);

  const { filters } = useSelector((state: Store) => state.projects);
  const currentAccount = useSelector(selectCurrentAccount);

  const allFolders = useSelector(selectAllFolders);
  const projects = useSelector(selectAllProjects);
  const folders = getRealFolders(allFolders);
  const reports = useSelector(selectAllReports);
  const multiProjects = getMultiProjectFolders(allFolders);

  const handleSearchTrigger = () => {
    if (value) {
      closeOptions();
      navigate(`/${filters}?${SEARCH_KEY}=${value}`);
    }
  };

  /**
   * Event handler for selecting autocomplete option
   * and redirect users to folder/project page
   */
  const handleOnChange = (_: React.SyntheticEvent<Element, Event>, selected: SearchOption) => {
    if (selected?.EntityType === "all") {
      return;
    }

    const resourceId = selected?.ID;
    if (selected?.EntityType === "folder") {
      const selectedInCurrentAccount = selected.Account.ID === currentAccount?.ID;
      dispatch(updateFilter(selectedInCurrentAccount ? "workspace" : "shared"));

      closeOptions();
      navigate(`/folders/${resourceId}`);
      return;
    }
    navigate(`/project/${resourceId}`);
  };

  const setSuggestionsListDebounced = useDebouncedCallback((searchValue: string) => {
    const suggestedProjects = searchCollectionByKey(projects, searchValue, "ProjectName");
    const suggestedFolders = searchCollectionByKey(folders, searchValue, "Name");
    const suggestedMultiProjects = searchCollectionByKey(multiProjects, searchValue, "Name");
    const suggestedReports = searchCollectionByKey(reports, searchValue, "Name");

    const allResultsCount = suggestedProjects.length + suggestedFolders.length + suggestedMultiProjects.length + suggestedReports.length;
    setResultCount({
      projects: suggestedProjects.length,
      folders: suggestedFolders.length,
      multiProjects: suggestedMultiProjects.length,
      reports: suggestedReports.length,
      all: allResultsCount,
    });

    setSuggestionsList({
      projects: suggestedProjects.slice(0, MAX_SUGGESTIONS_PER_GROUP).map((project) => ({ ...project, path: getPath(folders, project.FolderID) })),
      folders: suggestedFolders.slice(0, MAX_SUGGESTIONS_PER_GROUP),
      multiProjects: suggestedMultiProjects.slice(0, MAX_SUGGESTIONS_PER_GROUP),
      reports: suggestedReports.slice(0, MAX_SUGGESTIONS_PER_GROUP).map((report) => ({ ...report, path: getPath(folders, report.Folder?.ID) })),
    });
  }, 500);

  /**
   * Event handler for text field.
   * Sets the search value to navigate to search page (immediate)
   * and sets suggestion list based on the search value (debounced)
   */
  const handleInputChange = async (searchValue: string) => {
    if (!isFoldersLoaded && !isFoldersLoading && !isFoldersError) {
      getFolders({ expanded: true });
    }
    if (!isProjectsLoaded && !isProjectsLoading && !isProjectsError) {
      getProjects(undefined);
    }
    setValue(searchValue);
    setSuggestionsListDebounced(searchValue);
  };

  const getOptionLabel = (option: SearchOption) => {
    switch (option.EntityType) {
      case "folder":
        return option.Name;
      case "project":
        return option.ProjectName;
      case "report":
        return option.Name;
      default:
        return value;
    }
  };

  const hasTruncatedGroup = useMemo(() => {
    return (
      suggestionsList.projects.length === MAX_SUGGESTIONS_PER_GROUP ||
      suggestionsList.multiProjects.length === MAX_SUGGESTIONS_PER_GROUP ||
      suggestionsList.folders.length === MAX_SUGGESTIONS_PER_GROUP ||
      suggestionsList.reports.length === MAX_SUGGESTIONS_PER_GROUP
    );
  }, [suggestionsList]);

  const options = [
    ...suggestionsList.projects,
    ...suggestionsList.multiProjects,
    ...suggestionsList.folders,
    ...suggestionsList.reports,
    ...(hasTruncatedGroup ? [{ EntityType: "all" as const }] : []),
  ];

  const getGroupCountForOption = (option: SearchOption): number => {
    if (option.EntityType === "folder") {
      if (option.MultiProject !== null) {
        return resultCount.multiProjects;
      }
      return resultCount.folders;
    }
    if (option.EntityType === "project") {
      return resultCount.projects;
    }
    if (option.EntityType === "report") {
      return resultCount.reports;
    }
    if (option.EntityType === "all") {
      return resultCount.all;
    }
    return 0;
  };

  const getGroupBy = (option: SearchOption): string => {
    const count = getGroupCountForOption(option);
    if (option.EntityType === "folder" && option.MultiProject !== null) {
      return `MARKET LENS (${count})`;
    }
    return `${option.EntityType.toLocaleUpperCase()} (${count})`;
  };

  return (
    <Autocomplete
      data-testid="global-search"
      id="global-search"
      disableClearable
      open={open && resultCount.all > 0}
      onOpen={() => {
        if (value) setOpen(true);
      }}
      className={className}
      onClose={closeOptions}
      onClick={handleSearchTrigger}
      onChange={handleOnChange}
      onKeyDown={(event) => event.key === "Enter" && handleSearchTrigger()}
      onInputChange={(_, newValue) => handleInputChange(newValue)}
      options={options}
      filterOptions={(filteredOptions) => filteredOptions}
      groupBy={getGroupBy}
      getOptionLabel={getOptionLabel}
      PaperComponent={(props) => <Paper {...props} />}
      renderInput={(params) => (
        <TextField
          {...params}
          sx={{ width: "100%", minWidth: 490 }}
          className={className ? `${className}-textfield` : ""}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <IconButton onClick={handleSearchTrigger} sx={{ mr: 0.5 }}>
                <Search />
              </IconButton>
            ),
            endAdornment: null,
          }}
          placeholder="Search for anything you have access to..."
        />
      )}
      renderOption={(props, option) =>
        option.EntityType === "all" ? (
          <SeeAllResultsButton {...props} data-testid="see-all-results" key="ALL" onClick={handleSearchTrigger}>
            see all results
          </SeeAllResultsButton>
        ) : (
          <SearchSuggestionsRow {...props} option={option} onClick={closeOptions} key={option.ID} />
        )
      }
      renderGroup={(params) => (
        <React.Fragment key={params.group}>
          {params.group !== "ALL" && <ListSubheader component="div">{params.group}</ListSubheader>}
          {params.children}
        </React.Fragment>
      )}
    />
  );
};

export default GlobalSearch;
